/**
 * The Cargo-derived visual fragments for `TextInput`. (kept in the
 * component as its original literals so Hubble is byte-for-byte unchanged — this
 * module only describes the look (underline-only, larger bold value, blue
 * focus accent), mirroring `apps/dashboard/src/Cargo/Controls/Input.tsx`.
 *
 * Colors reference the design tokens (defined in theme.css with light +
 * dark values), so the design adapts under `.dark`. Remaining caveat: we use
 * `font-bold` since Neue Haas Unica (Cargo renders bold at 600 via NHU) isn't
 * loaded in ui-core yet.
 */

export const ADORNMENT_COLOR = "text-muted-foreground";

export function inputBaseStyle(mono: boolean, compact = false): string {
  // Forms get the large Cargo value (text-xl); inside a dense control/filter
  // bar the field shrinks to text-sm so it lines up with sibling controls.
  const size = compact ? "text-sm" : "text-xl leading-7";
  return `${size} font-bold text-foreground placeholder:font-normal placeholder:text-faint focus:outline-none bg-transparent ${mono ? "font-mono" : ""}`;
}

/**
 * Underline for the bare `<input>` (simple path). Resting is 1px + `pb-[5px]`;
 * hover/focus thicken to 2px blue and drop to `pb-1` so the +1px border is
 * absorbed and the field's height (and value baseline) stays put — no jiggle.
 * Mirrors Cargo's Input. The fragment owns the bottom padding.
 */
export function underlineStyle(isError: boolean): string {
  return isError
    ? "border-b-2 border-red pb-1"
    : "border-b border-muted-foreground pb-[5px] hover:border-b-2 hover:border-brand hover:pb-1 focus:border-b-2 focus:border-brand focus:pb-1";
}

/** Same 1px↔2px underline with height-preserving `pb` shift, on focus-within. */
export function underlineWrapper(isError: boolean): string {
  return isError
    ? "border-b-2 border-red pb-1"
    : "border-b border-muted-foreground pb-[5px] hover:border-b-2 hover:border-brand hover:pb-1 focus-within:border-b-2 focus-within:border-brand focus-within:pb-1";
}

/**
 * the "compact" (boxed) field — the dense-form counterpart to the underline
 * above. It's the Cargo boxed input (postal-code / notes style) generalized to
 * the design tokens: a 1px border on the surface, blue focus, smaller
 * text. Used when a form opts into compact density; the comfortable default
 * stays the underline. `boxedBase` is the text/value styling, `boxedField`
 * / `boxedWrapper` the box (focus vs focus-within for the adornment path).
 */
export function boxedBase(mono: boolean, compact = false): string {
  // Control-bar boxes shrink their text too (not just height) so the density
  // change actually reads; forms keep the larger value.
  const size = compact ? "text-xs" : "text-sm";
  return `${size} font-medium text-foreground placeholder:font-normal placeholder:text-faint focus:outline-none bg-transparent ${mono ? "font-mono" : ""}`;
}

export function boxedField(isError: boolean, compact = false): string {
  return `${compact ? "h-[26px]" : "h-8"} rounded-md border bg-card transition-colors ${
    isError ? "border-red" : "border-border-strong focus:border-brand"
  }`;
}

export function boxedWrapper(isError: boolean, compact = false): string {
  return `${compact ? "h-[26px]" : "h-8"} rounded-md border bg-card transition-colors ${
    isError ? "border-red" : "border-border-strong focus-within:border-brand"
  }`;
}

/**
 * The two chrome fragments for `FieldShell`, which wraps a control we don't
 * own (a Stripe iframe). They differ from `boxedWrapper` / `underlineWrapper`
 * only in taking `focused` explicitly: a cross-origin iframe never triggers
 * `:focus-within`, so the caller drives the focus border off the embedded
 * control's own focus/blur events.
 */
export function shellBox(isError: boolean, focused: boolean): string {
  const border = isError
    ? "border-red"
    : focused
      ? "border-brand"
      : "border-border-strong focus-within:border-brand";
  return `h-8 rounded-md border bg-card px-3 transition-colors ${border}`;
}

export function shellUnderline(isError: boolean, focused: boolean): string {
  const underline = isError
    ? "border-b-2 border-red pb-1"
    : focused
      ? "border-b-2 border-brand pb-1"
      : "border-b border-muted-foreground pb-[5px] hover:border-b-2 hover:border-brand hover:pb-1";
  // `pt-2` is not decoration: an underlined field owns the gap between the
  // label and its value, and `FormItem` tucks its underlined label down with
  // `-mb-1` on that assumption. Without it the label sits on top of the value.
  // `min-h-[41px]` (pt-2 + leading-7 + pb-[5px] + border) is the height of an
  // underlined TextInput, so the shell lines up with the fields beside it and
  // holds its place before the embedded control has loaded.
  return `pt-2 min-h-[41px] ${underline}`;
}
